import React from 'react';
import { TableColumn, Text } from 'tea-component';
import BubbleEllipsis from '@/pages/datalab/components/BubbleEllipsis';
import { TableHoverFiledSearchBox } from '../../../Home/RecentlyAndShare';
import ActionCell from './ActionCell';
import type { IModelRecord } from '../types';
import moment from 'moment';

type TQuickValue = {
  creator_name: string[];
  deployment_stage: string[];
  published_version: string[];
};

interface IGetColumnsParams {
  formatMessage: (descriptor: { id: string; }, values?: Record<string, any>) => string;
  quickValue: TQuickValue;
  deploymentStageFilterData: Array<{ value: string; text: string; }>;
  publishVersionFilterData: Array<{ value: string; text: string; }>;
  creatorsFilterData: Array<{ value: string; text: string; }>;
  nameMap: Record<string, string>;
  gameCode: string;
  loadingState: any;
  actionsDetail: IModelRecord | null;
  isEn: boolean;
  onEdit: (record: IModelRecord) => void;
  onDetail: (uuid: string, name: string) => void;
  onAccess: (record: IModelRecord) => void;
  onDeleteClick: (record: IModelRecord) => void;
  onTest: (uuid: string) => void;
  onPublish: (uuid: string) => void;
  onUnpublish: (uuid: string) => void;
  onActionDetail: (record: IModelRecord) => void;
  handleOnClickCreateSearch: (key: 'deployment_stage' | 'published_version' | 'creator_name', value: string) => void;
}

export const getColumns = ({
  formatMessage,
  quickValue,
  deploymentStageFilterData,
  publishVersionFilterData,
  creatorsFilterData,
  nameMap,
  loadingState,
  actionsDetail,
  isEn,
  onEdit,
  onDetail,
  onAccess,
  onDeleteClick,
  onTest,
  onPublish,
  onUnpublish,
  onActionDetail,
  handleOnClickCreateSearch,
}: IGetColumnsParams): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      key: 'name',
      width: 390,
      fixed: 'left',
      header: formatMessage({ id: 'dbl_model_management_name' }),
      render: (record: any) => {
        const name = record?.name || '-';
        return name ? (
          <BubbleEllipsis content={name} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {name}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'description',
      width: 360,
      header: formatMessage({ id: 'dbl_model_management_description' }),
      render: (record: any) => {
        const desc = record?.description || '-';
        return desc ? (
          <BubbleEllipsis content={desc} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {desc}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'deployment_stage',
      width: isEn ? 200 : 180,
      header: (
        <div key={JSON.stringify(quickValue?.deployment_stage)}>
          <TableHoverFiledSearchBox
            {...{
              data: deploymentStageFilterData,
              handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('deployment_stage', value),
              value: quickValue?.deployment_stage,
              buttonText: formatMessage({ id: 'dbl_model_management_deployment_stage' }),
            }}
          />
        </div>
      ),
      render: (record: any) => {
        const colorMap: any = {
          draft: '#EDA53A',
          tested: '#6A96F5',
          published: '#29B569',
        };
        const name = nameMap?.[record?.deployment_stage] || '-';
        const color = colorMap?.[record?.deployment_stage] || '#BBBBBB';
        return (
          <div className='dbl-model-management-deployment-stage'>
            <div className='dbl-model-management-deployment-stage-icon' style={{ background: color }}></div>
            <div className='dbl-model-management-deployment-stage-name'>{name}</div>
          </div>
        );
      },
    },
    {
      key: 'published_version',
      width: isEn ? 200 : 180,
      header: (
        <div key={JSON.stringify(quickValue?.published_version)}>
          <TableHoverFiledSearchBox
            {...{
              data: publishVersionFilterData,
              handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('published_version', value),
              value: quickValue?.published_version,
              buttonText: formatMessage({ id: 'dbl_model_management_publish_version' }),
            }}
          />
        </div>
      ),
      render: (record: any) => {
        const publishedVersion = record?.published_version || '-';
        return publishedVersion ? (
          <BubbleEllipsis content={publishedVersion} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {publishedVersion}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'creator_name',
      width: 120,
      header: (
        <div key={JSON.stringify(quickValue?.creator_name)}>
          <TableHoverFiledSearchBox
            {...{
              data: creatorsFilterData,
              handleOnClickCreateSearch: (value: string) => handleOnClickCreateSearch('creator_name', value),
              value: quickValue?.creator_name,
              buttonText: formatMessage({ id: 'dbl_model_management_creator' }),
            }}
          />
        </div>
      ),
      render: (record: any) => {
        const creatorName = record?.creator_name || '-';
        return creatorName ? (
          <BubbleEllipsis content={creatorName} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {creatorName}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'updated_at',
      header: formatMessage({ id: 'dbl_model_management_update_time' }),
      width: 180,
      render: (record: any) => {
        const updatedAt = record?.updated_at || '-';
        const time = moment(updatedAt).format('YYYY-MM-DD HH:mm:ss');
        return moment(updatedAt)?.isValid() ? time : updatedAt;
      }
    },
    {
      key: 'permissions',
      width: isEn ? 220 : 205,
      fixed: 'right',
      header: formatMessage({ id: 'dbl_model_management_action' }),
      render: (record: IModelRecord) => (
        <ActionCell
          record={record}
          actionsDetail={actionsDetail}
          loadingState={loadingState}
          onEdit={onEdit}
          onDetail={onDetail}
          onAccess={onAccess}
          onDeleteClick={onDeleteClick}
          onTest={onTest}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
          onActionDetail={onActionDetail}
          formatMessage={formatMessage}
        />
      ),
    },
  ];

  return columns;
};


